import { defineStore } from 'pinia';
import apiService from '@/services/api.service';
import { Config } from '@/config';
import { useAuthStore, useCommunityStore } from '.';
import { Constants } from '@/constants';
import router from '@/router';

import type { Locales, LocalizableString, ConsultationDto } from '@/services/types/api.types';
import { useI18n } from 'vue-i18n';
import { FileUploaderFactory } from '@/factories/file-uploader.factory';
import { ProcessorType, type SavingOptions } from '@/services/types/file-uploader.types';

export interface ConsultationTemplatesState {
  consultationTemplates: ConsultationDto[];
  isLoading: boolean;
  itemLoadingStates: Set<number>;
}

function getStatuses(): any[] {
  const { t } = useI18n();
  return [
    { label: t('consultation.status.options.pre-launch'), value: Constants.Consultation.Status.PreLaunch },
    { label: t('consultation.status.options.ongoing'), value: Constants.Consultation.Status.Ongoing },
    { label: t('consultation.status.options.completed'), value: Constants.Consultation.Status.Completed },
    { label: t('consultation.status.options.archived'), value: Constants.Consultation.Status.Archived },
  ];
}

function insertOrUpdate<T>(array: T[], item: T, key: keyof T) {
  const index = array.findIndex((i) => i[key] === item[key]);
  if (index !== -1) {
    array[index] = item;
  } else {
    array.push(item);
  }
}

const findConsultationTemplate = (consultationTemplates: ConsultationDto[], consultationTemplateId: number) => {
  return consultationTemplates.find((x) => {
    return x.id == consultationTemplateId;
  });
};

export const useConsultationTemplatesStore = defineStore({
  id: 'consultationTemplates',
  state: (): ConsultationTemplatesState => ({
    consultationTemplates: [],
    isLoading: false,
    itemLoadingStates: new Set<number>(),
  }),
  getters: {
    isItemLoading: (state) => (id: number) => state.itemLoadingStates.has(id),
    getItem:
      (state) =>
      (id: number): ConsultationDto | undefined =>
        state.consultationTemplates.find((c) => c.id === id),
    getConsultationStatuses: (state) => getStatuses,
    previewURL: (state) => (consultationTemplateId: number) => {
      const consultationTemplate = findConsultationTemplate(state.consultationTemplates, consultationTemplateId);
      const communityStore = useCommunityStore();
      if (consultationTemplate) {
        const communityUrl = communityStore.previewURL(consultationTemplate.community_id);
        return `${communityUrl}/consultation-templates/${consultationTemplate.slug}`;
      }
    },
  },
  actions: {
    async fetchConsultation(id: number) {
      return this.fetchById(id);
    },
    async fetchById(id: number) {
      this.itemLoadingStates.add(id);
      try {
        const { data } = await apiService.get<ConsultationDto>(`${Config.api.v2}/consultation-templates/${id}`);
        insertOrUpdate(this.consultationTemplates, data, 'id');
      } catch (error) {
        console.error(error);
      } finally {
        this.itemLoadingStates.delete(id);
      }
    },
    async fetchAll() {
      this.isLoading = true;
      try {
        const { data } = await apiService.get<ConsultationDto[]>(`${Config.api.v2}/consultation-templates/`);
        for (const consultationTemplate of data) {
          insertOrUpdate(this.consultationTemplates, consultationTemplate, 'id');
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },
    async delete(consultationTemplateId: number) {
      try {
        await apiService.delete(`${Config.api.v2}/consultation-templates/${consultationTemplateId}`);
        const index = this.consultationTemplates.findIndex((s) => s.id === consultationTemplateId);
        this.consultationTemplates.splice(index, 1);
      } catch (error) {
        console.error(error);
      }
    },
    create(options: {
      title?: LocalizableString;
      description?: LocalizableString;
      cloneFromId?: number;
      language_enabled: Locales[];
    }): ConsultationDto {
      const authStore = useAuthStore();
      return {
        community_id: authStore.currentCommunity ?? -1,
        slug: '',
        status: Constants.Consultation.Status.PreLaunch,
        title: options.title ?? { en: '', fr: '' },
        description: options.description ?? { en: '', fr: '' },
        type: Constants.Consultation.Type.Public,
        participation_mode: Constants.Consultation.ParticipationMode.Open,
        options: {
          show_private_everyone: false,
          automatic_publish: false,
          citizen_proposal: true,
          map: false,
          anonymous_comments: false,
          mailingList: false,
          guided_consultation: {
            resultsMode: Constants.Consultation.GuidedConsultation.ResultsMode.CompletedOnly,
            visibility: { ruleSets: [] },
          },
        },
        order_num: -1,
        attachmentsOpened: false,
        private_email_allowed: { email: [], domain: [] },
        banner_file: null,
        open_graph_file: null,
        projects: [],
        user_permissions: [],
        language_enabled: options.language_enabled,
        conclusion_message: {
          enabled: false,
          content: { en: '', fr: '' },
        },
        is_template: 1,
        clone_from_id: options.cloneFromId,
      };
    },
    async save(consultationTemplate: ConsultationDto) {
      try {
        let consultationTemplateId = consultationTemplate.id;
        if (consultationTemplateId) {
          await apiService.put<ConsultationDto>(
            `${Config.api.v2}/consultation-templates/${consultationTemplateId}`,
            consultationTemplate,
          );
        } else {
          const { data } = await apiService.post<ConsultationDto>(
            `${Config.api.v2}/consultation-templates`,
            consultationTemplate,
          );
          consultationTemplateId = data.id;
          router.push({ name: 'consultation_template_details', params: { id: consultationTemplateId } });
        }
        await this.fetchById(consultationTemplateId!);
        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    },
    async saveBanner(consultationTemplateId: number, options: SavingOptions): Promise<boolean> {
      try {
        await FileUploaderFactory.create(options.version).upload({
          image: options.image,
          method: apiService.post,
          processorType: ProcessorType.Multer,
          url: `${Config.api.v2}/consultation-templates/${consultationTemplateId}/banner`,
        });
        await this.fetchById(consultationTemplateId);
        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    },
    async removeBanner(consultationTemplateId: number) {
      try {
        await apiService.delete(`${Config.api.v2}/consultation-templates/${consultationTemplateId}/banner`);
        await this.fetchById(consultationTemplateId);
      } catch (error) {
        console.error(error);
      }
    },
    async saveOpenGraph(consultationTemplateId: number, options: SavingOptions): Promise<boolean> {
      try {
        await FileUploaderFactory.create(options.version).upload({
          image: options.image,
          method: apiService.post,
          processorType: ProcessorType.Multer,
          url: `${Config.api.v2}/consultation-templates/${consultationTemplateId}/open-graph`,
        });
        await this.fetchById(consultationTemplateId);
        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    },
    async removeOpenGraph(consultationTemplateId: number) {
      try {
        await apiService.delete(`${Config.api.v2}/consultation-templates/${consultationTemplateId}/open-graph`);
        await this.fetchById(consultationTemplateId);
      } catch (error) {
        console.error(error);
      }
    },
  },
});
